/**
 * Sidebar / navigation intent preload — i18n JSON + authorized route JS chunks.
 *
 * **Two systems, one entry point**
 * - i18n: `preloadNamespacesForRoute` (registry-derived map in `appModuleI18n.js`)
 * - chunks: `preloadRouteChunk` (dynamic imports in `routeChunkLoaders.js`)
 *
 * Chunk preload is best-effort and never blocks navigation. i18n preload may
 * gate first authenticated paint via `useRouteNamespacesReady`, but in-session
 * hover uses background preload only.
 *
 * @module routes/routePreloaders
 * @see docs/translate/preload.md
 * @see docs/route-chunk-preload.md
 */

import { preloadNamespacesForRoute } from "i18n";
import { canAccessModule, resolveModuleKeyForPath } from "modules/appModuleAccess";
import {
  preloadRouteChunk,
  resolveRouteIdForModuleKey,
} from "./routeChunkLoaders";

// /** Dev-only logging — search `[chunk-preload]` to remove after validation. */
// const logChunkPreload = (...args) => {
//   if (process.env.NODE_ENV === "development") console.log("[chunk-preload]", ...args);
// };

/** pathname → in-flight i18n preload promise (dedupes rapid sidebar hovers). */
const routeNamespacePreloadPromises = new Map();

/** Strip query/hash and ensure leading slash. */
const normalizePathname = (pathname = "/") => {
  const [pathWithoutQuery] = String(pathname || "/").split(/[?#]/);
  const normalized = pathWithoutQuery || "/";
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

/**
 * Preload i18n JSON for a destination path (deduped per pathname).
 * Fail-open: errors log and resolve so navigation is never blocked.
 *
 * @param {string} pathname
 * @returns {Promise<void>}
 */
export function preloadRouteNamespaces(pathname) {
  const normalizedPathname = normalizePathname(pathname);

  if (routeNamespacePreloadPromises.has(normalizedPathname)) {
    return routeNamespacePreloadPromises.get(normalizedPathname);
  }

  const preloadPromise = preloadNamespacesForRoute(normalizedPathname)
    .catch((error) => {
      console.error("Route namespace preload failed", {
        pathname: normalizedPathname,
        error,
      });
    })
    .finally(() => {
      // Allow a future hover to re-trigger if namespaces were not yet cached.
      routeNamespacePreloadPromises.delete(normalizedPathname);
    });

  routeNamespacePreloadPromises.set(normalizedPathname, preloadPromise);
  return preloadPromise;
}

/**
 * Sidebar hover/focus intent: warm i18n + (when allowed) the matching route chunk.
 *
 * Always starts i18n preload. Chunk preload runs only when:
 * 1. pathname resolves to a registry module, and
 * 2. `canAccessModule(moduleKey, userData)` is true, and
 * 3. a `routeId` exists for the current viewport variant
 *
 * @param {string} pathname — sidebar item `to` / target URL
 * @param {Object} userData — current auth store user payload
 * @param {Object} [options]
 * @param {"desktop"|"mobile"} [options.variant="desktop"]
 * @returns {Promise<void>}
 */
export function preloadRouteIntent(pathname, userData, { variant = "desktop" } = {}) {
  const normalizedPathname = normalizePathname(pathname);
  const namespacePromise = preloadRouteNamespaces(normalizedPathname);
  const moduleKey = resolveModuleKeyForPath(normalizedPathname);

  // No module or no access — still preload translations for the destination URL.
  if (!moduleKey || !canAccessModule(moduleKey, userData)) {
    // logChunkPreload(`sidebar intent: ${normalizedPathname} (i18n only)`);
    return namespacePromise;
  }

  const routeId = resolveRouteIdForModuleKey(moduleKey, variant);
  if (!routeId) {
    // e.g. mobile viewport + module without mobileEnabled — i18n only.
    // logChunkPreload(`sidebar intent: ${normalizedPathname} (i18n only)`);
    return namespacePromise;
  }

  // logChunkPreload(`sidebar intent: ${normalizedPathname} → ${routeId} (${variant})`);

  const chunkPromise = preloadRouteChunk(routeId).catch((error) => {
    console.error("Route chunk intent preload failed", {
      pathname: normalizedPathname,
      routeId,
      error,
    });
  });

  return Promise.all([namespacePromise, chunkPromise]).then(() => undefined);
}
